import { Component, OnInit } from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { of } from 'rxjs'
import { catchError, take, timeout } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { ConfigOptionsKeys } from 'src/app/shared/constants'
import { WholeNumberValidator } from 'src/app/shared/validators/whole-number.validator'
import {
  ChildRegistrationFormConfig,
  FamilySettingsWebOption
} from '../configuration-option'
import { configurationOptionActions } from '../configuration-option.actions'
import { configurationOptionSelectors } from '../configuration-option.selectors'
import { ConfigurationOptionService } from '../configuration-option.service'
import { normalizeNumber } from '../../../shared/utils/numbers'

type ChildRegistrationFieldKey = keyof ChildRegistrationFormConfig
type ChildRegistrationFormFieldState = {
  visible: boolean
  required: boolean
}
type ChildRegistrationFormState = Record<
  ChildRegistrationFieldKey,
  ChildRegistrationFormFieldState
>
type FamilySettingsFormValue = Omit<
  FamilySettingsWebOption,
  'childRegistrationForm'
> & {
  childRegistrationForm: ChildRegistrationFormState
}

@Component({
  selector: 'configuration-option-family-tab',
  templateUrl: './show.component.html'
})
export class ConfigurationOptionFamilyTabComponent implements OnInit {
  public form: UntypedFormGroup
  public isCollapsed = false
  public accessRights = AccessRights
  public configFamilySettings: FamilySettingsFormValue
  public readonly childRegistrationFields: Array<{
    key: ChildRegistrationFieldKey
    label: string
  }> = [
    { key: 'middleName', label: 'general.middleName' },
    { key: 'lastName', label: 'general.lastName' },
    { key: 'birthDate', label: 'general.birthDate' }
  ]

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private configOptionService: ConfigurationOptionService,
    private notificationService: NotificationService,
    private actions$: Actions
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      isActive: [false, []],
      requireChildApproval: [false, []],
      allowChildren: [false, []],
      childDefaultCategory: [
        null,
        [WholeNumberValidator(), Validators.min(0), Validators.max(9), Validators.required]
      ],
      maxMembersPerFamily: [
        null,
        [WholeNumberValidator(), Validators.min(0), Validators.required]
      ],
      childRegistrationForm: this.buildChildRegistrationFormGroup()
    })
    this.store
      .select(
        configurationOptionSelectors.selectByKeyWithType<FamilySettingsWebOption>(
          ConfigOptionsKeys.FAMILY_SETTINGS
        )
      )
      .pipe(take(1))
      .subscribe((config) => {
        this.configFamilySettings = this.buildFormPatchValue(config)
      })
  }

  public onFormSubmit(): void {
    if (this.form.invalid) {
      return
    }

    this.actions$
      .pipe(
        ofType(configurationOptionActions.upsertMany),
        take(1),
        timeout(1000),
        catchError(() => of(null))
      )
      .subscribe((action) =>
        action
          ? this.notificationService.emit('alerts.success', 'success')
          : null
      )

    this.configOptionService.updateOrCreateMany({
      [ConfigOptionsKeys.FAMILY_SETTINGS]: this.buildFamilySettingsPayload()
    })
  }

  public patchValueForm(field: string, value: boolean): void {
    this.form.controls[field]?.patchValue(value)
  }

  public patchChildRequired(
    field: ChildRegistrationFieldKey,
    value: boolean
  ): void {
    this.form
      .get(['childRegistrationForm', field, 'required'])
      ?.patchValue(value)
  }

  public patchChildVisibility(
    field: ChildRegistrationFieldKey,
    visible: boolean
  ): void {
    this.form
      .get(['childRegistrationForm', field, 'visible'])
      ?.patchValue(visible)
    if (!visible) {
      this.form
        .get(['childRegistrationForm', field, 'required'])
        ?.patchValue(false)
    }
  }

  private buildChildRegistrationFormGroup(): UntypedFormGroup {
    const controls = this.childRegistrationFields.reduce(
      (acc, field) => {
        acc[field.key] = this.fb.group({
          visible: [false, []],
          required: [false, []]
        })
        return acc
      },
      {} as Record<ChildRegistrationFieldKey, UntypedFormGroup>
    )

    return this.fb.group(controls)
  }

  private buildFamilySettingsPayload(): FamilySettingsWebOption {
    const rawValue = this.form.value as FamilySettingsFormValue

    return {
      ...rawValue,
      childDefaultCategory: normalizeNumber(rawValue.childDefaultCategory),
      maxMembersPerFamily: normalizeNumber(
        rawValue.maxMembersPerFamily
      ) as number,
      childRegistrationForm: this.buildChildRegistrationForm(
        rawValue.childRegistrationForm
      )
    }
  }

  private buildChildRegistrationForm(
    formValue: ChildRegistrationFormState
  ): ChildRegistrationFormConfig {
    const registrationForm: ChildRegistrationFormConfig = {}

    this.childRegistrationFields.forEach(({ key }) => {
      const fieldState = formValue?.[key]
      if (fieldState?.visible) {
        registrationForm[key] = {
          required: Boolean(fieldState.required)
        }
      }
    })

    return registrationForm
  }

  private normalizeFamilySettings(value: any): FamilySettingsWebOption {
    if (!value) {
      return value
    }

    const normalized = { ...value }

    if (
      normalized.childRegistrationForm?.birthDate &&
      !normalized.childRegistrationForm?.birthDate
    ) {
      normalized.childRegistrationForm = {
        ...normalized.childRegistrationForm,
        birthDate: normalized.childRegistrationForm.birthDate
      }
    }

    return normalized
  }

  private buildFormPatchValue(
    value?: FamilySettingsWebOption
  ): FamilySettingsFormValue {
    const normalized = value ? this.normalizeFamilySettings(value) : null

    return {
      isActive: normalized?.isActive ?? false,
      requireChildApproval: normalized?.requireChildApproval ?? false,
      allowChildren: normalized?.allowChildren ?? false,
      childDefaultCategory: normalized?.childDefaultCategory ?? null,
      maxMembersPerFamily: normalized?.maxMembersPerFamily ?? null,
      childRegistrationForm: this.buildChildRegistrationFormState(
        normalized?.childRegistrationForm
      )
    }
  }

  private buildChildRegistrationFormState(
    value?: ChildRegistrationFormConfig
  ): ChildRegistrationFormState {
    return this.childRegistrationFields.reduce((acc, field) => {
      const configField = value?.[field.key]
      acc[field.key] = {
        visible: Boolean(configField),
        required: Boolean(configField?.required)
      }
      return acc
    }, {} as ChildRegistrationFormState)
  }
}
